import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/data.service';
import { firstValueFrom } from 'rxjs';

/**
 *  Jogosultság-szerkesztő: jogkör × képernyő mátrix, alrendszerenként.
 *
 *  Eddig az `access` táblát csak adatbázisból lehetett írni. A mátrix ugyanazokat a
 *  sorokat kezeli, amelyekből a bal oldali menü és a belső fülrendszerek dolgoznak,
 *  így ami itt ki van pipálva, az azonnal látszik (a felhasználó következő
 *  bejelentkezésekor / menüfrissítésekor).
 *
 *  A mentés csak a MEGVÁLTOZOTT cellákat küldi el, hogy két admin párhuzamos
 *  szerkesztése ne írja felül egymás munkáját teljes mátrixcserével.
 */
@Component({
  selector: 'app-admin-jogosultsag',
  templateUrl: './jogosultsag.component.html',
  styleUrls: ['./jogosultsag.component.scss'],
})
export class AdminJogosultsagComponent implements OnInit {

  alrendszerek: string[] = [];
  alrendszer = '';
  intezmeny = '';
  intezmenyek: any[] = [];

  jogkorok: any[] = [];
  csoportok: any[] = [];
  matrix: any = {};

  toltodik = false;
  mentesFut = false;
  hiba = '';
  szuro = '';

  /** A megváltoztatott cellák: "jogkor||url" → allow. Mentéskor csak ezek mennek el. */
  private valtozasok = new Map<string, boolean>();

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = sajat?.intezmenyId || '';
    this.alrendszer = (await this.data.getFromStorage('currentMode')) || 'SULINFORM';

    try {
      this.intezmenyek = (await this.data.getFromStorage('intezmenyek')) || [];
    } catch { this.intezmenyek = []; }

    try {
      this.alrendszerek = await firstValueFrom(this.http.get<string[]>('/api/jogosultsag/alrendszerek'));
    } catch { this.alrendszerek = [this.alrendszer]; }

    await this.betolt();
  }

  async betolt() {
    // NEM némán állunk meg: hiányzó intézmény vagy alrendszer esetén eddig üres képernyő
    // maradt (se adat, se hibaüzenet), ami kívülről „nem tölt be"-nek látszik. A
    // sajatIntezmeny a tárolóból jön, és belépési/intézményváltási hiba után üres lehet.
    if (!this.alrendszer || !this.intezmeny) {
      this.hiba = !this.intezmeny
        ? 'Nincs kiválasztott intézmény (a tárolt sajatIntezmeny üres). Válassz intézményt '
          + 'a fejlécben, vagy lépj ki és be – utána a jogosultságok betölthetők.'
        : 'Nincs kiválasztott alrendszer.';
      this.jogkorok = []; this.csoportok = []; this.matrix = {};
      this.toltodik = false;
      return;
    }
    this.toltodik = true;
    this.hiba = '';
    this.valtozasok.clear();
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/jogosultsag/matrix/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`));
      this.jogkorok = r.jogkorok || [];
      this.csoportok = r.csoportok || [];
      this.matrix = r.matrix || {};
    } catch (e: any) {
      // A pontos okot is kiírjuk: a 0/502/504 azt jelenti, hogy a VÁLASZ nem ért célba
      // (proxy vagy időtúllépés), a szerver üzenete pedig jellemzően séma- vagy
      // jogosultsági hibát nevez meg. Enélkül csak annyi látszott, hogy „nem sikerült".
      const kod = Number(e?.status || 0);
      const uzenet = e?.error?.error || e?.message || '';
      this.hiba = [0, 502, 503, 504].includes(kod)
        ? `A kérés nem ért célba (${kod || 'megszakadt kapcsolat'}). Ha ismétlődik, a `
          + 'webkiszolgáló időkorlátja vágja el a választ.'
        : `A jogosultságok betöltése nem sikerült${kod ? ` (HTTP ${kod})` : ''}`
          + `${uzenet ? ': ' + uzenet : '.'}`;
      this.jogkorok = []; this.csoportok = []; this.matrix = {};
    } finally {
      this.toltodik = false;
    }
  }

  /* ── „Mit kap ténylegesen?" – a futásidejű eredmény visszaellenőrzése ── */
  ellenorzottJogkor = '';
  ellenorzesLista: string[] | null = null;
  ellenorzesFut = false;

  /**
   *  Ugyanazt a végpontot kérdezi, amit a belépéskor a kliens: így a képernyőn látott
   *  pipák és a TÉNYLEGES jogok egymás mellé tehetők. Az eltérés oka jellemzően az
   *  öröklés (jogkor.link) vagy a csillag – mindkettőt a lista fedi fel.
   */
  async ellenorzes(jogkor: string) {
    this.ellenorzottJogkor = jogkor;
    this.ellenorzesLista = null;
    if (!jogkor) return;
    this.ellenorzesFut = true;
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/access/${encodeURIComponent(this.alrendszer)}/${encodeURIComponent(jogkor)}/${this.intezmeny}`));
      const lista = r?.url === null || r?.url === undefined ? null : JSON.parse(r.url || '[]');
      this.ellenorzesLista = Array.isArray(lista) ? lista.map((x: any) => String(x)).sort() : [];
    } catch (e) {
      this.ellenorzesLista = [];
    } finally {
      this.ellenorzesFut = false;
    }
  }

  /** Meg van-e engedve a (jogkör, url) pár? */
  engedve(jogkor: string, url: string): boolean {
    return !!this.matrix?.[jogkor]?.[url];
  }

  /**
   *  Az öröklés jelzése: a `jogkor.link` mezőn keresztül a jogkör megkapja a szülő
   *  jogköre engedélyeit is. Ezt a szerver oldja fel, a mátrixban nem szerepel –
   *  itt csak jelezzük, hogy a felhasználó ne keresse hiába a pipát.
   */
  oroklott(jogkor: any, url: string): boolean {
    if (this.engedve(jogkor.jogkor, url)) return false;
    let szulo = jogkor.link;
    const latott = new Set<string>();
    while (szulo && !latott.has(szulo)) {
      latott.add(szulo);
      if (this.matrix?.[szulo]?.[url] || this.matrix?.[szulo]?.['*']) return true;
      szulo = this.jogkorok.find((j) => j.jogkor === szulo)?.link;
    }
    return false;
  }

  /**
   *  Van-e a ponthoz SZÜLŐ FEJLÉC, amit nem engedélyeztünk ennek a jogkörnek?
   *
   *  A bal menü a fejlécen (`0/…`) keresztül rajzolja ki a gyerek elemeket. Ha a
   *  fejléc nincs engedve, a gyerek menüpont eddig NÉMÁN eltűnt – hiába volt bepipálva.
   *  (A felület már felső szinten mutatja az ilyet, de a jogot érdemes rendbe tenni.)
   */
  fejlecHianyzik(jogkor: string, p: any): boolean {
    if (!p?.szulo_url) return false;
    if (!this.engedve(jogkor, p.url) && !this.oroklottUrl(jogkor, p.url)) return false;
    return !this.engedve(jogkor, p.szulo_url) && !this.oroklottUrl(jogkor, p.szulo_url);
  }

  /** A figyelmeztető jel súgószövege. */
  fejlecSugo(p: any): string {
    return `Ehhez a menühöz a(z) "${p?.szulo_cim}" csoportfejléc joga is kell `
      + `(${p?.szulo_url}), különben nem a csoportjában jelenik meg.`;
  }

  /** Öröklött-e a jog (url szerint, a jogkör-objektum nélkül). */
  oroklottUrl(jogkor: string, url: string): boolean {
    const j = this.jogkorok.find((x) => x.jogkor === jogkor);
    return j ? this.oroklott(j, url) : false;
  }

  /** A csillag mindent visz: ha egy jogkörnek megvan, a többi pipa csak dísz. */
  mindentLat(jogkor: string): boolean {
    return !!this.matrix?.[jogkor]?.['*'];
  }

  valt(jogkor: string, url: string) {
    if (!this.matrix[jogkor]) this.matrix[jogkor] = {};
    const uj = !this.matrix[jogkor][url];
    this.matrix[jogkor][url] = uj ? 1 : 0;
    this.valtozasok.set(`${jogkor}||${url}`, uj);
  }

  get valtozottDb(): number { return this.valtozasok.size; }

  szurtPontok(csoport: any): any[] {
    const k = this.szuro.trim().toLowerCase();
    if (!k) return csoport.pontok;
    return csoport.pontok.filter((p: any) =>
      String(p.cim || '').toLowerCase().includes(k) || String(p.url || '').toLowerCase().includes(k));
  }

  /**
   *  A kódba drótozott, mai viselkedés feltöltése access-sorokká. Idempotens és nem
   *  vesz el semmit – új intézménynél / alrendszernél ezzel indul a szerkesztés.
   */
  async alapertek() {
    this.mentesFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/jogosultsag/alapertek/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`, {}));
      this.data.sendToast(r?.felvett
        ? `${r.felvett} jogosultság feltöltve a mai állapot szerint.`
        : 'Nem kellett pótolni semmit – minden alapértelmezett jog megvan.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A feltöltés nem sikerült.', 'error');
    } finally {
      this.mentesFut = false;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
   *  MENÜPÓTLÁS
   *
   *  A bal menü a `menu` táblából épül, INTÉZMÉNYENKÉNT külön sorokkal – az
   *  access csak szűr rajta. Ha egy intézménynél nincs felvéve a menüsor, hiába
   *  van valakinek csillag joga: nincs mit megmutatni. Új intézménynél és új
   *  feladatellátásnál pontosan ez a tünet, ezért itt előbb MEGMUTATJUK a hiányt,
   *  és csak külön kattintásra pótoljuk egy működő intézmény menüje alapján.
   * ═════════════════════════════════════════════════════════════════════ */

  menuHiany: any = null;
  menuHianyFut = false;
  menuPotlasFut = false;
  menuNyitva = false;
  /** Amit pótolni kérünk. Alapból minden hiányzó be van jelölve. */
  menuValasztott = new Set<string>();

  async menuHianyBetolt(nyit = true) {
    if (!this.alrendszer || !this.intezmeny) return;
    this.menuHianyFut = true;
    if (nyit) this.menuNyitva = true;
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/jogosultsag/menu-hianyok/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`));
      this.menuHiany = r;
      this.menuValasztott = new Set<string>((r?.hianyzik || []).map((h: any) => h.url));
    } catch (e: any) {
      this.menuHiany = null;
      this.data.sendToast(e?.error?.error || 'A menühiány lekérése nem sikerült.', 'error');
    } finally {
      this.menuHianyFut = false;
    }
  }

  menuValt(url: string) {
    if (this.menuValasztott.has(url)) this.menuValasztott.delete(url);
    else this.menuValasztott.add(url);
  }

  /** A fejléc kijelölése a gyerekeit is viszi – fejléc nélkül azok nem a helyükre kerülnének. */
  menuMind(be: boolean) {
    this.menuValasztott = be
      ? new Set<string>((this.menuHiany?.hianyzik || []).map((h: any) => h.url))
      : new Set<string>();
  }

  async menuPotlas() {
    const urlok = [...this.menuValasztott];
    if (!urlok.length) return;

    // A gyerek menüpont a csoportfejlécén keresztül rajzolódik ki: ha a fejléc
    // kimarad a pótlásból, a pont felső szinten jelenne meg. Inkább szólunk.
    const hianyzoFejlec = (this.menuHiany?.hianyzik || [])
      .filter((h: any) => h.szulo_url && urlok.includes(h.url) && !urlok.includes(h.szulo_url)
        && (this.menuHiany?.hianyzik || []).some((x: any) => x.url === h.szulo_url))
      .map((h: any) => h.szulo_url);
    if (hianyzoFejlec.length && !confirm(
      `${[...new Set(hianyzoFejlec)].join(', ')} csoportfejléc nincs kijelölve, pedig tartozik hozzá `
      + 'pótolandó menüpont. Ezek felső szinten fognak megjelenni. Folytatod?')) return;

    this.menuPotlasFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/jogosultsag/menu-potlas/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`, { urlok }));
      this.data.sendToast(r?.felvett
        ? `${r.felvett} menüpont felvéve (${r.cel_intezmeny}). `
          + (r.hianyzik ? `Még ${r.hianyzik} hiányzik.` : 'Nincs több hiány.')
        : 'Nem kellett pótolni semmit.', 'success');
      await this.menuHianyBetolt(false);
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A menüpótlás nem sikerült.', 'error');
    } finally {
      this.menuPotlasFut = false;
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
   *  JOGKÖRÖK
   *
   *  Létrehozás, átnevezés, öröklési kapcsolat (link) és törlés. Az öröklés a
   *  futásidejű jogosultság-feloldás része: a `link`-en át a jogkör megkapja a
   *  fölérendelt jogkör engedélyeit is, ezért a mátrixban halvány pipaként látszik.
   * ═════════════════════════════════════════════════════════════════════ */

  jogkorNyitva = false;
  jogkorFut = false;
  ujJogkor: any = { jogkor: '', name: '', link: '', admin: false, sorrend: 10 };

  /** Az öröklési legördülőben önmaga nem szerepelhet. */
  valaszthatoSzulo(jogkor: string): any[] {
    return this.jogkorok.filter((j) => j.jogkor !== jogkor);
  }

  async jogkorLetrehoz() {
    const uj = { ...this.ujJogkor, jogkor: String(this.ujJogkor.jogkor || '').trim().toLowerCase() };
    if (!uj.jogkor || !uj.name) {
      this.data.sendToast('Az azonosító és a megjelenő név is kell.', 'error');
      return;
    }
    this.jogkorFut = true;
    try {
      await firstValueFrom(this.http.post(
        `/api/jogosultsag/jogkor/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`, uj));
      this.data.sendToast(`A(z) „${uj.name}" jogkör létrejött. Jogokat a mátrixban adj neki.`, 'success');
      this.ujJogkor = { jogkor: '', name: '', link: '', admin: false, sorrend: 10 };
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A jogkör létrehozása nem sikerült.', 'error');
    } finally {
      this.jogkorFut = false;
    }
  }

  async jogkorMent(j: any) {
    this.jogkorFut = true;
    try {
      await firstValueFrom(this.http.put(
        `/api/jogosultsag/jogkor/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`, {
          jogkor: j.jogkor,
          name: j.name,
          link: j.link || null,
          admin: !!Number(j.admin),
          sorrend: Number(j.sorrend) || 0,
        }));
      this.data.sendToast('A jogkör mentve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A jogkör mentése nem sikerült.', 'error');
      await this.betolt();
    } finally {
      this.jogkorFut = false;
    }
  }

  async jogkorTorol(j: any) {
    if (!confirm(`Törlöd a(z) „${j.name || j.jogkor}" jogkört? A hozzá tartozó jogosultságok is törlődnek.`)) return;
    this.jogkorFut = true;
    try {
      await firstValueFrom(this.http.delete(
        `/api/jogosultsag/jogkor/${encodeURIComponent(j.jogkor)}/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`));
      this.data.sendToast('A jogkör törölve.', 'success');
      await this.betolt();
    } catch (e: any) {
      // 409: használatban van. A szerver megmondja, mi tartja – ezt kell a felhasználó elé tenni.
      if (e?.status === 409) {
        const h = e?.error?.hasznalat || {};
        const reszlet = [
          h.dolgozo ? `${h.dolgozo} dolgozói besorolás` : '',
          h.szulo ? `${h.szulo} gondviselő` : '',
          h.oroklok?.length ? `öröklő jogkör: ${h.oroklok.join(', ')}` : '',
          h.jogok ? `${h.jogok} jogosultsági sor` : '',
        ].filter(Boolean).join('\n· ');
        if (!confirm(`A jogkör használatban van:\n· ${reszlet}\n\n`
          + 'Ha mégis törlöd, az érintettek elveszítik ezt a jogkört, az öröklés pedig megszakad. Törlöd?')) {
          this.jogkorFut = false;
          return;
        }
        try {
          await firstValueFrom(this.http.delete(
            `/api/jogosultsag/jogkor/${encodeURIComponent(j.jogkor)}/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}?kenyszer=1`));
          this.data.sendToast('A jogkör törölve.', 'success');
          await this.betolt();
        } catch (e2: any) {
          this.data.sendToast(e2?.error?.error || 'A törlés nem sikerült.', 'error');
        }
      } else {
        this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
      }
    } finally {
      this.jogkorFut = false;
    }
  }

  async mentes() {
    if (!this.valtozasok.size) return;
    this.mentesFut = true;
    try {
      const valtozasok = [...this.valtozasok].map(([kulcs, allow]) => {
        const [jogkor, url] = kulcs.split('||');
        return { jogkor, url, allow };
      });
      await firstValueFrom(this.http.put(
        `/api/jogosultsag/matrix/${encodeURIComponent(this.alrendszer)}/${this.intezmeny}`, { valtozasok }));
      this.valtozasok.clear();
      this.data.sendToast('A jogosultságok mentve. Az érintett felhasználóknál a következő '
        + 'menüfrissítéskor (újbóli belépéskor) lép életbe.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.mentesFut = false;
    }
  }
}
